import React, { useState, useRef, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Bot, Check, PenLine, Loader2, FileText, Eye, Send } from 'lucide-react';
import { base44 } from '@/api/base44Client';
import ReactMarkdown from 'react-markdown';

export default function BotDocumentReviewDialog({ open, onOpenChange, documentOutput, taskId, onApprove, onCancel }) {
  const [mode, setMode] = useState('review'); // 'review' | 'feedback' | 'regenerating'
  const [feedback, setFeedback] = useState('');
  const [currentContent, setCurrentContent] = useState('');
  const [currentFileName, setCurrentFileName] = useState('');
  const [revisionCount, setRevisionCount] = useState(0);

  useEffect(() => {
    if (open && documentOutput) {
      setCurrentContent(documentOutput.content || '');
      setCurrentFileName(documentOutput.suggested_file_name || 'document.md');
      setMode('review');
      setFeedback('');
      setRevisionCount(0);
    }
    if (!open) {
      setMode('review');
      setFeedback('');
      setCurrentContent('');
      setCurrentFileName('');
      setRevisionCount(0);
    }
  }, [open, documentOutput]);

  const handleRequestRevision = async () => {
    if (!feedback.trim()) return;
    setMode('regenerating');

    try {
      const llmResult = await base44.integrations.Core.InvokeLLM({
        prompt: `You are revising a document based on user feedback.\n\nDocument title: "${currentFileName}"\n\nHere is the current document:\n\n---\n${currentContent}\n---\n\nThe user's feedback/requested changes (revision #${revisionCount + 1}):\n${feedback}\n\nPlease produce the COMPLETE revised document incorporating all the requested changes. Output ONLY the document content in markdown format, no commentary or explanations before/after.`,
      });
      if (llmResult) {
        setCurrentContent(typeof llmResult === 'string' ? llmResult : llmResult.toString());
      }
      setRevisionCount(prev => prev + 1);
      setFeedback('');
      setMode('review');
    } catch (error) {
      alert(`Failed to revise document: ${error.message}`);
      setMode('review');
    }
  };

  const handleApprove = () => {
    onApprove({
      ...documentOutput,
      content: currentContent,
      suggested_file_name: currentFileName,
      task_id: taskId
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="bots-botdocumentreviewdialog-dialog" className="bg-slate-900 border-slate-800 text-slate-100 max-w-3xl max-h-[90vh] flex flex-col p-0 overflow-hidden">
        <DialogHeader className="p-4 border-b border-slate-800">
          <DialogTitle className="flex items-center gap-2">
            <Eye className="w-5 h-5 text-cyan-400" />
            Review Document Before Saving
          </DialogTitle>
          <div className="flex items-center gap-2 mt-1">
            <FileText className="w-3 h-3 text-slate-400" />
            <span className="text-sm text-slate-400">{currentFileName}</span>
            {revisionCount > 0 && (
              <span className="text-xs text-amber-400 ml-2">(Revision #{revisionCount})</span>
            )}
          </div>
        </DialogHeader>

        {mode === 'regenerating' ? (
          <div className="flex-1 flex flex-col items-center justify-center py-16 gap-3">
            <Loader2 className="w-8 h-8 text-purple-400 animate-spin" />
            <p className="text-sm text-slate-400">Revising document based on your feedback...</p>
          </div>
        ) : (
          <>
            {/* Document Preview */}
            <div className="flex-1 overflow-y-auto min-h-0 p-6">
              <div className="prose prose-invert prose-sm max-w-none">
                <ReactMarkdown>{currentContent}</ReactMarkdown>
              </div>
            </div>

            {/* Feedback Section */}
            {mode === 'feedback' && (
              <div className="p-4 border-t border-slate-800 bg-amber-500/5">
                <div className="flex items-center gap-2 mb-2 text-sm text-amber-300">
                  <PenLine className="w-4 h-4" />
                  Request Changes
                </div>
                <Textarea data-testid="bots-botdocumentreviewdialog-textarea"
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  placeholder="Describe what you'd like changed, added, or removed..."
                  className="bg-slate-800 border-slate-700 text-slate-100 min-h-[80px] mb-3"
                  autoFocus
                />
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => { setMode('review'); setFeedback(''); }}
                    className="border-slate-700 text-slate-300 hover:bg-slate-800"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleRequestRevision}
                    disabled={!feedback.trim()}
                    className="bg-amber-600 hover:bg-amber-700"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Submit Feedback & Revise
                  </Button>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            {mode === 'review' && (
              <div className="p-4 border-t border-slate-800">
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => { onCancel?.(); onOpenChange(false); }}
                    className="border-slate-700 text-slate-300 hover:bg-slate-800"
                  >
                    Discard
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setMode('feedback')}
                    className="flex-1 border-amber-500/30 text-amber-400 hover:bg-amber-500/20"
                  >
                    <PenLine className="w-4 h-4 mr-2" />
                    Request Changes
                  </Button>
                  <Button
                    onClick={handleApprove}
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                  >
                    <Check className="w-4 h-4 mr-2" />
                    Approve & Proceed to Save
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}